import React, { useCallback, useEffect, useState } from 'react';
import { Download, FileCheck2, FileUp, Landmark, Receipt, ScrollText } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { formatCurrency } from '../../utils/currency';
import SearchableSelect from '../../components/crm/SearchableSelect';
import { Btn, INPUT, LABEL, errMsg } from '../../components/crm/billing/ui';
import Tabs from '../../components/ui/Tabs';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';

const TABS = [
  { key: 'gstr1', label: 'GSTR-1', icon: ScrollText },
  { key: 'gstr2b', label: 'GSTR-2B match', icon: FileCheck2 },
  { key: 'tdsin', label: 'TDS by customers', icon: Receipt },
  { key: 'tdsout', label: 'TDS deducted (26Q)', icon: Landmark },
];
const inr = (v) => formatCurrency(v || 0, 'INR');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const thisMonth = () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const currentQuarter = () => {
  const d = new Date(); const m = d.getMonth(); const y = m >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return { fy: `${y}-${String(y + 1).slice(2)}`, quarter: m >= 3 ? Math.floor((m - 3) / 3) + 1 : 4 };
};
const fyOptions = () => { const y = new Date().getFullYear(); return [y + 1, y, y - 1, y - 2].map((s) => `${s - 1}-${String(s).slice(2)}`); };

async function download(url, params, filename) {
  try {
    const res = await api.get(url, { params, responseType: 'blob' });
    const href = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = href; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  } catch (e) {
    let msg = 'Please try again';
    try { msg = JSON.parse(await e.response.data.text()).message || msg; } catch (x) { /* not JSON */ }
    alertService.error('Download failed', msg);
  }
}

const Card = ({ label, value, hint, tone }) => (
  <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-3">
    <div className="text-[11px] uppercase tracking-wide text-[#706E6B]">{label}</div>
    <div className={cn('text-[17px] font-semibold tabular-nums', tone === 'bad' && 'text-[#BA0517]', tone === 'good' && 'text-[#2E844A]')}>{value}</div>
    {hint && <div className="text-[11px] text-[#706E6B]">{hint}</div>}
  </div>
);

function Gstr1Tab() {
  const [period, setPeriod] = useState(thisMonth());
  const [s, setS] = useState(null);
  useEffect(() => {
    setS(null);
    api.get('/crm/compliance/gstr1', { params: { period } }).then((r) => setS(r.data.summary)).catch((e) => alertService.error('Could not build GSTR-1', errMsg(e)));
  }, [period]);
  const rows = s ? [
    ['B2B — registered customers (4A)', s.b2b.documents, s.b2b.txval, s.b2b.tax],
    ['B2C large — inter-state over ₹1 lakh (5)', s.b2cl.documents, s.b2cl.txval, s.b2cl.tax],
    ['B2C others, by state and rate (7)', `${s.b2cs.rows} row(s)`, s.b2cs.txval, s.b2cs.tax],
    ['Exports (6A)', s.exports.documents, s.exports.txval, s.exports.tax],
    ['Credit notes (9B)', s.creditNotes.documents, s.creditNotes.txval, s.creditNotes.tax],
  ] : [];
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-2">
        <div><label className={LABEL} htmlFor="g1-p">Return period</label><input id="g1-p" type="month" className={INPUT} value={period} onChange={(e) => setPeriod(e.target.value)} /></div>
        <Btn primary onClick={() => download('/crm/compliance/gstr1', { period, format: 'json' }, `GSTR1_${period}.json`)}><Download size={13} className="inline -mt-0.5 mr-1" />JSON for the GST portal</Btn>
        <Btn onClick={() => download('/crm/compliance/gstr1', { period, format: 'csv' }, `GSTR1_${period}.csv`)}>CSV to review</Btn>
      </div>
      {s && (
        <>
          {s.warning && <p className="text-[13px] px-3 py-2 rounded border bg-[#FEF8E8] border-[#F5D58B] text-[#7A5300]">{s.warning}</p>}
          <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-x-auto">
            <table className="w-full text-[13px] sm:min-w-[35rem]">
              <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr><th className="px-3 py-2 text-left">Table</th><th className="px-3 py-2 text-right">Documents</th><th className="px-3 py-2 text-right">Taxable value</th><th className="px-3 py-2 text-right">Tax</th></tr></thead>
              <tbody>{rows.map((r) => <tr key={r[0]} className="border-t border-[#F3F2F2]"><td className="px-3 py-2">{r[0]}</td><td className="px-3 py-2 text-right tabular-nums">{r[1]}</td><td className="px-3 py-2 text-right tabular-nums">{inr(r[2])}</td><td className="px-3 py-2 text-right tabular-nums">{inr(r[3])}</td></tr>)}</tbody>
            </table>
          </div>
          <p className="text-[12px] text-[#706E6B]">
            HSN summary: {s.hsnRows} row(s){s.missingHsn ? ` · ${s.missingHsn} invoice(s) have lines without an HSN/SAC code` : ''} · {s.cancelled} cancelled invoice(s) counted in the documents-issued table.
            Upload the JSON in the GST portal’s GSTR-1 “Prepare offline” page and check the totals there before filing.
          </p>
          {s.skipped.length > 0 && (
            <details className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-3 text-[12px]" open>
              <summary className="cursor-pointer font-semibold">{s.skipped.length} document(s) need attention</summary>
              {s.skipped.map((x, i) => <div key={i} className="border-t border-[#EEF1F6] py-1.5"><strong>{x.number}</strong> — {x.reason}</div>)}
            </details>
          )}
        </>
      )}
    </div>
  );
}

const STATUS = {
  matched: ['Matched', 'bg-[#E6F4EA] text-[#2E844A]'],
  mismatch: ['Differs', 'bg-[#FEF8E8] text-[#7A5300]'],
  missing_in_books: ['Not in your books', 'bg-[#E0F2FE] text-[#0176D3]'],
  missing_in_2b: ['Supplier hasn’t filed', 'bg-[#FEF1EE] text-[#BA0517]'],
};

function Gstr2bTab() {
  const [imports, setImports] = useState([]);
  const [current, setCurrent] = useState(null);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const loadList = useCallback(() => api.get('/crm/compliance/gstr2b').then((r) => setImports(r.data.imports || [])).catch(() => {}), []);
  useEffect(() => { loadList(); }, [loadList]);
  const open = (id) => api.get(`/crm/compliance/gstr2b/${id}`).then((r) => { setCurrent(r.data.import); setFilter(''); }).catch((e) => alertService.error('Could not open', errMsg(e)));
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await api.post('/crm/compliance/gstr2b', { json: await file.text() });
      setCurrent(r.data.import); setFilter(''); loadList();
    } catch (e) { alertService.error('Import failed', errMsg(e)); } finally { setBusy(false); }
  };
  const rows = (current?.rows || []).filter((r) => !filter || r.status === filter);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-2">
        <label className={cn('h-9 px-3 rounded bg-[#0176D3] text-white text-[13px] font-semibold flex items-center gap-1 cursor-pointer', busy && 'opacity-60 pointer-events-none')}>
          <FileUp size={14} />{busy ? 'Matching…' : 'Upload GSTR-2B JSON'}
          <input type="file" accept=".json,application/json" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
        </label>
        {imports.length > 0 && (
          <div className="w-64"><label className={LABEL} htmlFor="g2-hist">Earlier imports</label>
            <select id="g2-hist" className={INPUT} value={current?._id || ''} onChange={(e) => e.target.value && open(e.target.value)}>
              <option value="">Choose…</option>{imports.map((i) => <option key={i._id} value={i._id}>{i.period.slice(0, 2)}/{i.period.slice(2)} · imported {fmtDate(i.createdAt)}</option>)}
            </select></div>
        )}
      </div>
      {!current && <p className="text-[13px] text-[#706E6B]">Download GSTR-2B as JSON from the GST portal (Returns → GSTR-2B), then upload it here. Each supplier invoice is matched to your purchase bills by the supplier’s GSTIN and bill number, so you only claim credit your suppliers have actually reported.</p>}
      {current && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
            <Card label="Invoices in 2B" value={current.summary.invoices} hint={`${inr(current.summary.portalTax)} tax`} />
            <Card label="Matched" value={current.summary.matched} tone="good" />
            <Card label="Differ" value={current.summary.mismatch} tone={current.summary.mismatch ? 'bad' : undefined} />
            <Card label="Not in your books" value={current.summary.missingInBooks} hint="Record these bills" />
            <Card label="Supplier hasn’t filed" value={current.summary.missingIn2b} tone={current.summary.missingIn2b ? 'bad' : undefined} hint={`${inr(current.summary.itcAtRisk)} credit at risk`} />
          </div>
          <div className="flex gap-1 flex-wrap">
            {[['', 'All'], ...Object.entries(STATUS).map(([k, v]) => [k, v[0]])].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setFilter(k)} className={cn('h-8 px-3 rounded text-[12px] font-semibold border whitespace-nowrap shrink-0', filter === k ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-white border-[#D8DDE6]')}>{l}</button>
            ))}
          </div>
          <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-x-auto">
            <table className="w-full text-[12px] sm:min-w-[45rem]">
              <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr>{['Supplier', 'Invoice', 'Date', 'Tax filed', 'Tax in books', 'Status'].map((h) => <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-[#F3F2F2] align-top">
                    <td className="px-3 py-2">{r.supplier || r.vendor || '—'}<span className="block text-[11px] text-[#706E6B]">{r.ctin}</span></td>
                    <td className="px-3 py-2">{r.inum}{r.billNumber && <span className="block text-[11px] text-[#706E6B]">Bill {r.billNumber}</span>}</td>
                    <td className="px-3 py-2">{r.date}</td>
                    <td className="px-3 py-2 tabular-nums">{r.portalTax !== undefined ? inr(r.portalTax) : '—'}</td>
                    <td className="px-3 py-2 tabular-nums">{r.bookTax !== undefined ? inr(r.bookTax) : '—'}</td>
                    <td className="px-3 py-2"><span className={cn('text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase', STATUS[r.status][1])}>{STATUS[r.status][0]}</span>
                      {r.differences?.map((d) => <span key={d} className="block text-[11px] text-[#706E6B]">{d}</span>)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!rows.length && <p className="text-[13px] text-[#706E6B] text-center py-6">Nothing here.</p>}
          </div>
        </>
      )}
    </div>
  );
}

function TdsInTab() {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get('/crm/compliance/tds-receivable').then((r) => setD(r.data)).catch((e) => alertService.error('Could not load', errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="text-[13px] text-[#706E6B]">Loading…</p>;
  const save = async () => {
    try { await api.post(`/crm/compliance/tds-receivable/${edit.id}/certificate`, { number: edit.number, date: edit.date || undefined }); setEdit(null); load(); } catch (e) { alertService.error('Not saved', errMsg(e)); }
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        <Card label="TDS deducted by customers" value={inr(d.total)} />
        <Card label="TDS receivable in books" value={inr(d.ledgerBalance)} hint="Claim against Form 26AS" />
        <Card label="Certificates not received" value={d.certificatesPending} tone={d.certificatesPending ? 'bad' : 'good'} hint="Form 16A from the customer" />
      </div>
      <p className="text-[12px] text-[#706E6B]">Record TDS when entering a receipt (“TDS deducted by customer”). Check each quarter’s total against your Form 26AS before claiming it in your income-tax return.</p>
      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-x-auto">
        <table className="w-full text-[12px] sm:min-w-[45rem]">
          <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr>{['Date', 'Quarter', 'Customer', 'Section', 'Settled', 'TDS', 'Certificate'].map((h) => <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr></thead>
          <tbody>
            {d.rows.map((r) => (
              <tr key={r.id} className="border-t border-[#F3F2F2]">
                <td className="px-3 py-2">{fmtDate(r.date)}</td><td className="px-3 py-2">{r.quarter}</td><td className="px-3 py-2">{r.customer}</td>
                <td className="px-3 py-2">{r.section || '—'}</td><td className="px-3 py-2 tabular-nums">{formatCurrency(r.gross, r.currency)}</td><td className="px-3 py-2 tabular-nums">{formatCurrency(r.tds, r.currency)}</td>
                <td className="px-3 py-2">
                  {edit?.id === r.id ? (
                    <div className="flex gap-1 items-center">
                      <input aria-label="Certificate number" className={cn(INPUT, 'h-8 w-32')} value={edit.number} onChange={(e) => setEdit({ ...edit, number: e.target.value })} placeholder="Certificate no." />
                      <input aria-label="Certificate date" type="date" className={cn(INPUT, 'h-8 w-36')} value={edit.date} onChange={(e) => setEdit({ ...edit, date: e.target.value })} />
                      <Btn primary className="h-8 px-2" onClick={save}>Save</Btn>
                    </div>
                  ) : r.certificate ? (
                    <button type="button" className="text-left" onClick={() => setEdit({ id: r.id, number: r.certificate.number, date: r.certificate.date?.slice(0, 10) || '' })}>{r.certificate.number}<span className="block text-[11px] text-[#706E6B]">{fmtDate(r.certificate.date)}</span></button>
                  ) : (
                    <button type="button" className="text-[#0176D3] hover:underline" onClick={() => setEdit({ id: r.id, number: '', date: '' })}>Mark received</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!d.rows.length && <p className="text-[13px] text-[#706E6B] text-center py-6">No customer has deducted TDS yet.</p>}
      </div>
    </div>
  );
}

function TdsOutTab() {
  const [q, setQ] = useState(currentQuarter());
  const [d, setD] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState('');
  useEffect(() => { api.get('/crm/purchases/vendors', { params: { limit: 500 } }).then((r) => setVendors((r.data.vendors || []).map((v) => ({ _id: v._id, name: v.name })))).catch(() => {}); }, []);
  useEffect(() => {
    setD(null);
    api.get('/crm/compliance/tds-26q', { params: q }).then((r) => setD(r.data)).catch((e) => alertService.error('Could not load', errMsg(e)));
  }, [q]);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-2">
        <div><label className={LABEL} htmlFor="q-fy">Financial year</label><select id="q-fy" className={INPUT} value={q.fy} onChange={(e) => setQ({ ...q, fy: e.target.value })}>{fyOptions().map((f) => <option key={f}>{f}</option>)}</select></div>
        <div><label className={LABEL} htmlFor="q-q">Quarter</label><select id="q-q" className={INPUT} value={q.quarter} onChange={(e) => setQ({ ...q, quarter: Number(e.target.value) })}>{[['1', 'Q1 Apr–Jun'], ['2', 'Q2 Jul–Sep'], ['3', 'Q3 Oct–Dec'], ['4', 'Q4 Jan–Mar']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
        <Btn primary onClick={() => download('/crm/compliance/tds-26q', { ...q, format: 'csv' }, `26Q_${q.fy}_Q${q.quarter}.csv`)}><Download size={13} className="inline -mt-0.5 mr-1" />26Q deductee data (CSV)</Btn>
      </div>
      {d && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <Card label="TDS deducted" value={inr(d.total)} hint={`${d.rows.length} payment(s)`} />
            <Card label="Vendors without a valid PAN" value={d.missingPan.length} tone={d.missingPan.length ? 'bad' : 'good'} hint={d.missingPan.join(', ') || 'All set'} />
          </div>
          <p className="text-[12px] text-[#706E6B]">Bring this into your TDS return software (RPU) with the challan details from your bank. After the return is processed, download Form 16A for each vendor from TRACES.</p>
          <div className="flex flex-wrap items-end gap-2 bg-white border border-[#E5E5E5] rounded-lg p-3 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
            <div className="w-72"><span className={LABEL}>Vendor statement of TDS for the quarter</span><SearchableSelect value={vendorId} onChange={setVendorId} options={vendors} placeholder="Choose vendor…" /></div>
            <Btn disabled={!vendorId} onClick={() => download('/crm/compliance/tds-statement', { ...q, vendorId }, `TDS-statement-${q.fy}-Q${q.quarter}.pdf`)}>Download PDF</Btn>
            <p className="text-[11px] text-[#706E6B] w-full">A working statement to send vendors until the official Form 16A is available.</p>
          </div>
          <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-x-auto">
            <table className="w-full text-[12px] sm:min-w-[45rem]">
              <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr>{['Date', 'Section', 'Vendor', 'PAN', 'Paid', 'Rate', 'TDS'].map((h) => <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr></thead>
              <tbody>{d.rows.map((r) => (
                <tr key={r.serial} className="border-t border-[#F3F2F2]">
                  <td className="px-3 py-2">{fmtDate(r.paymentDate)}</td><td className="px-3 py-2">{r.section || '—'}</td><td className="px-3 py-2">{r.deductee}</td>
                  <td className={cn('px-3 py-2', !r.pan && 'text-[#BA0517]')}>{r.pan || 'Missing'}</td><td className="px-3 py-2 tabular-nums">{inr(r.amountPaid)}</td><td className="px-3 py-2">{r.rate}%</td><td className="px-3 py-2 tabular-nums">{inr(r.tds)}</td>
                </tr>
              ))}</tbody>
            </table>
            {!d.rows.length && <p className="text-[13px] text-[#706E6B] text-center py-6">No TDS deducted on vendor payments this quarter.</p>}
          </div>
        </>
      )}
    </div>
  );
}

export default function CompliancePage() {
  const [tab, setTab] = useState('gstr1');
  return (
    <div className={PAGE}>
      <PageHeader title="GST & TDS returns" subtitle="Prepare GSTR-1, check input credit against GSTR-2B, and keep TDS in order." icon={FileCheck2} />
      <Tabs tabs={TABS} value={tab} onChange={setTab} />
      {tab === 'gstr1' && <Gstr1Tab />}
      {tab === 'gstr2b' && <Gstr2bTab />}
      {tab === 'tdsin' && <TdsInTab />}
      {tab === 'tdsout' && <TdsOutTab />}
    </div>
  );
}
